// cashierBTransactions.js — Pharmacy sales desk.
//
// Rewired onto the money engine (spec §13 step 4). The pharmacy desk carried the
// worst of the audit, and every one of those defects is closed here:
//
//  • C2 — the stock decrement was a non-atomic read-modify-write from an in-memory
//    cache loaded at page init, writing an ABSOLUTE value. Stock 10, B1 sells 3
//    (writes 7), B2 (cache still 10) sells 2 (writes 8): five units sold, three
//    conjured from nothing. STOCK IS NOW A FOLD of the ledger (spec §4.1). There is
//    no `drugs.current_stock` to race on — migration 0008 drops the column.
//
//  • C3 — sale and stock decrement were not atomic and the failure was reported as
//    success: the stock update error was `console.error`d, execution continued, and
//    « Vente complétée avec succès ! » was shown. There is no second write any
//    more. One event moves both the money and the goods.
//
//  • C4 — offline pharmacy items were written as `{name, quantity, unit_price,
//    total, unit}` with NO id, so every sync called the stock RPC with `undefined`,
//    swallowed the error per item, and still marked the row `'synced'`. Lines are
//    now the spec §6 shape and `db/ledger-write.js` refuses anything else.
//
//  • C1 — the "any insert error means we are offline" branch is DELETED.
//  • H3 — the cart is cleared on success and the committed receipt is locked.
//  • M5 — post-commit side effects live outside the try that reports failure.
//  • M6 — the drug-search regex is escaped.
//  • M7 — no unescaped `innerHTML` interpolation of a drug or patient name.
//
// KEPT, because the audit names them as already right: cart-side stock validation
// that accounts for the quantity already in the cart, out-of-stock disabling in
// both the grid and the autocomplete, and the submit button disabled before the
// await.
//
// One thing that deliberately did NOT change: the client still refuses to sell
// beyond its own known stock — a best-effort gate with honest scope (fully
// effective per device, best-effort across devices, spec §4.3). What is gone is
// any pretence that this gate is authority: an offline oversell is a fact about
// the world and the server RECORDS it and raises a red row (spec §4), it never
// refuses it.

import { supabase } from './supabaseClient.js';
import { showStatus, formatCurrency } from './utils.js';

import { db } from './db/schema.js';
import { recordSale, getAllEvents } from './db/index.js';
import { getEngine, collectionGate } from './ui/engine-boot.js';
import { messageFor, messageForError, UI_STRINGS } from './ui/engine-messages.js';
import { businessDateOf, foldMoney, foldStock, formatReceiptNo } from './money/index.js';

/** Low-stock threshold, in units. */
const LOW_STOCK_THRESHOLD = 20;

/**
 * Escape text destined for an `innerHTML` template (audit M7). Drug names are
 * admin-supplied and these receipts render in the admin's own session.
 * @param {*} value
 * @returns {string}
 */
function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Escape a string for literal use inside a `RegExp` (audit M6).
 *
 * Typing `(` into the drug search threw `SyntaxError` from an unescaped
 * `new RegExp`, and drug names commonly contain parentheses — so the search box
 * broke on exactly the input it was built for.
 * @param {string} value
 * @returns {string}
 */
function escapeRegExp(value) {
    return String(value == null ? '' : value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Coerce a price-list value to whole FCFA, or `null`. See the sibling note in
 * `cashierATransactions.js`: refusing beats rounding (audit M2).
 * @param {*} value
 * @returns {number|null}
 */
function toWholeFcfa(value) {
    const n = typeof value === 'number' ? value : Number(String(value ?? '').trim());
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return null;
    return n;
}

/**
 * Groupe les milliers, et RIEN d'autre.
 *
 * `utils.formatCurrency` (fr-CM → « 1 500 FCFA ») est laissé intact : d'autres
 * écrans et les tests s'appuient dessus, et la liste `<select>` de secours s'en
 * sert encore. Les écrans de caisse composent eux-mêmes l'unité, parce que la
 * maquette la veut tantôt en suffixe atténué (`<span>`), tantôt collée au
 * nombre, tantôt absente (lignes du reçu).
 */
const fmtNo = new Intl.NumberFormat('fr-FR');

/**
 * Feuille de style du document d'impression (spéc. §10).
 *
 * Autonome — la fenêtre pop-up ne partage aucune feuille avec l'application, à
 * l'exception de `css/fonts.css` que le document lie lui-même pour Poppins.
 * TOUT est en noir pur : une tête thermique n'a pas de gris, un #616161 sort
 * tramé et sale. Le bloc est volontairement d'un seul tenant pour qu'un
 * `[data-paper="58"]` puisse un jour s'y ajouter sans le disperser.
 */
const PRINT_CSS = `
/* Pas de "size": "80mm auto" est invalide (une longueur ne se combine pas avec
   auto) et Chromium jette alors toute la déclaration. Sur un rouleau thermique
   c'est le pilote qui fixe le format ; le corps porte la largeur de 80mm. */
@page { margin: 0; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; background: #fff; }
body {
    width: 80mm;
    color: #000;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 12px;
    line-height: 1.45;
    font-variant-numeric: tabular-nums;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
}
.receipt-thermal { width: 80mm; padding: 5mm 4mm 3mm; color: #000; text-align: left; }
.rt-head { text-align: center; }
.rt-mark { width: 14mm; height: 14mm; object-fit: contain; display: block; margin: 0 auto 4px; }
.rt-brand { font-family: 'Poppins', sans-serif; font-size: 16px; font-weight: 700; letter-spacing: 1.2px; line-height: 1.2; }
.rt-line { font-size: 11px; }
.rt-head .rt-line:first-of-type { margin-top: 3px; }
.rt-rule { border-top: 1px dashed #000; margin: 9px 0; }
.rt-id { text-align: center; }
.rt-doctype { font-size: 11px; font-weight: 700; letter-spacing: 0.14em; }
.rt-number { font-family: 'Poppins', sans-serif; font-size: 19px; font-weight: 700; letter-spacing: 1px; margin-top: 2px; }
.rt-meta { display: flex; flex-direction: column; gap: 2px; }
.rt-row { display: flex; justify-content: space-between; gap: 8px; }
.rt-row > span:last-child { text-align: right; }
.rt-row--strong > span:last-child { font-weight: 700; }
.rt-cols { display: flex; justify-content: space-between; font-size: 10px; font-weight: 700; letter-spacing: 0.1em; margin-bottom: 6px; }
.rt-items { display: flex; flex-direction: column; gap: 7px; }
.rt-item-name { font-weight: 700; line-height: 1.3; }
.rt-item-line { display: flex; justify-content: space-between; gap: 8px; }
.rt-solid { border-top: 1px solid #000; margin: 9px 0; }
.rt-total { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; font-family: 'Poppins', sans-serif; font-size: 17px; font-weight: 700; }
.rt-double { height: 2px; border-top: 1px solid #000; border-bottom: 1px solid #000; margin-top: 3px; }
.rt-thanks { text-align: center; margin-top: 11px; font-size: 11px; line-height: 1.5; }
.rt-important { border: 1px solid #000; padding: 5px 6px; margin-top: 9px; text-align: center; }
.rt-important-title { font-size: 10px; font-weight: 700; letter-spacing: 0.1em; }
.rt-important-body { font-size: 11px; line-height: 1.4; margin-top: 2px; }
.rt-note { border: 1px solid #000; padding: 5px 6px; margin-top: 9px; font-size: 11px; line-height: 1.4; text-align: center; }
/* Le bord déchiré est un ornement d'aperçu. Le rouleau se coupe tout seul. */
.rt-zigzag { display: none; }
`;

export const CashierBTransactions = {
    // Cache DOM elements
    elements: {},
    selectedDrugs: [],
    currentTotal: 0,
    drugsData: {},
    allDrugs: [], // Store all drugs for filtering
    /** Folded per-drug positions, keyed by drug id (spec §4.1). NOT a balance. */
    stockPositions: {},
    currentStockFilter: 'all',
    // Autocomplete state
    selectedDrug: null,
    autocompleteResults: [],
    autocompleteFocusIndex: -1,
    /** Latest collection-gate verdict. */
    collectionState: null,
    /** True while a committed receipt is on screen — the modal is then locked. */
    receiptCommitted: false,

    // Initialize the module
    init() {
        // Cache elements
        this.elements = {
            // Main form elements
            transactionForm: document.getElementById('cashier-b-transaction-form'),
            customerName: document.getElementById('customer-name'),
            prescriptionNo: document.getElementById('prescription-no'),
            customerAge: document.getElementById('customer-age'),
            customerSex: document.getElementById('customer-sex'),
            drugSelect: document.getElementById('drug-select'),
            drugSearchInput: document.getElementById('drug-search-input'),
            drugAutocompleteDropdown: document.getElementById('drug-autocomplete-dropdown'),
            clearDrugSearch: document.getElementById('clear-drug-search'),
            quantityInput: document.getElementById('drug-quantity'),
            maxQuantity: document.getElementById('max-quantity'),
            addDrugBtn: document.getElementById('add-drug-btn'),
            selectedDrugsList: document.getElementById('selected-drugs-list'),
            totalAmount: document.getElementById('pharmacy-total-amount'),
            submitBtn: document.getElementById('submit-pharmacy-btn'),
            clearBtn: document.getElementById('clear-pharmacy-btn'),
            cartCount: document.getElementById('cart-count-b'),
            printPreviewBtn: document.getElementById('print-preview-btn-b'),

            // New grid elements
            drugsGrid: document.getElementById('drugs-grid'),
            drugSearch: document.getElementById('drug-search'),
            stockTabs: document.getElementById('drug-stock-tabs'),
            drugsLoading: document.getElementById('drugs-loading'),

            // Mini stats
            todayCount: document.getElementById('cashier-b-today-count'),
            todayTotal: document.getElementById('cashier-b-today-total'),
            todayPending: document.getElementById('cashier-b-today-pending'),
            drugsCount: document.getElementById('cashier-b-drugs-count'),
            lowStockCount: document.getElementById('cashier-b-low-stock'),

            // Collection gate banner
            gateBanner: document.getElementById('cashier-b-gate-banner'),

            // Receipt modal
            receiptModal: document.getElementById('pharmacy-receipt-modal'),
            receiptContent: document.getElementById('pharmacy-receipt-content'),
            printReceiptBtn: document.getElementById('print-pharmacy-receipt'),
            closeReceiptBtn: document.getElementById('close-pharmacy-receipt'),
            newTransactionBtn: document.getElementById('new-pharmacy-transaction')
        };

        // Set up event listeners
        this.setupEventListeners();

        // Load drugs into select and grid
        this.loadDrugs();

        // Load mini stats
        this.loadMiniStats();

        // The collection gate, before the desk is usable (spec §7).
        void this.refreshGate();

        console.log('CashierBTransactions module initialized');
    },

    // Set up all event listeners
    setupEventListeners() {
        // Form actions
        this.elements.addDrugBtn?.addEventListener('click', () => this.addDrug());
        this.elements.clearBtn?.addEventListener('click', () => this.clearTransaction());
        this.elements.transactionForm?.addEventListener('submit', (e) => this.handleSubmit(e));

        // Autocomplete search input
        this.elements.drugSearchInput?.addEventListener('input', (e) => {
            this.handleAutocompleteSearch(e.target.value);
        });

        // Autocomplete keyboard navigation
        this.elements.drugSearchInput?.addEventListener('keydown', (e) => {
            this.handleAutocompleteKeydown(e);
        });

        // Focus/blur for autocomplete dropdown
        this.elements.drugSearchInput?.addEventListener('focus', () => {
            if (this.elements.drugSearchInput.value.length > 0) {
                this.showAutocompleteDropdown();
            }
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            const wrapper = document.getElementById('drug-autocomplete-wrapper');
            if (wrapper && !wrapper.contains(e.target)) {
                this.hideAutocompleteDropdown();
            }
        });

        // Clear search button
        this.elements.clearDrugSearch?.addEventListener('click', () => {
            this.clearDrugSelection();
        });

        // Allow Enter key to add drug from quantity input
        this.elements.quantityInput?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.addDrug();
            }
        });

        // Grid search functionality
        this.elements.drugSearch?.addEventListener('input', (e) => {
            this.filterDrugs(e.target.value, this.currentStockFilter);
        });

        // Stock filter tabs
        this.elements.stockTabs?.addEventListener('click', (e) => {
            if (e.target.classList.contains('category-tab')) {
                this.elements.stockTabs.querySelectorAll('.category-tab').forEach(tab => {
                    tab.classList.remove('active');
                });
                e.target.classList.add('active');

                this.currentStockFilter = e.target.dataset.stock;
                const searchValue = this.elements.drugSearch?.value || '';
                this.filterDrugs(searchValue, this.currentStockFilter);
            }
        });

        // Receipt modal actions
        this.elements.printReceiptBtn?.addEventListener('click', () => this.printReceipt());
        this.elements.closeReceiptBtn?.addEventListener('click', () => this.closeReceiptModal());
        this.elements.newTransactionBtn?.addEventListener('click', () => this.startNewTransaction());

        // Print preview button
        this.elements.printPreviewBtn?.addEventListener('click', () => this.showReceiptPreview());

        // Modal backdrop click — IGNORED for a committed receipt (audit H3).
        this.elements.receiptModal?.addEventListener('click', (e) => {
            if (e.target === this.elements.receiptModal) this.closeReceiptModal();
        });
    },

    // ---- stock, as a projection ---------------------------------------------

    /**
     * This device's known position for one drug.
     *
     * `positions[id]` is `fold(events)` — `sale −qty`, `void +qty`, `restock +qty`,
     * `stock_adjustment ±qty`. A drug with no events folds to 0, which is the
     * truth: this device knows of nothing on that shelf.
     * @param {string} drugId
     * @returns {number} May be NEGATIVE — an offline oversell is recorded, not hidden.
     */
    stockOf(drugId) {
        const position = this.stockPositions[drugId];
        return Number.isFinite(position) ? position : 0;
    },

    /** How many units of `drugId` are already in the cart. */
    cartQtyOf(drugId) {
        const line = this.selectedDrugs.find(d => d.id === drugId);
        return line ? line.quantity : 0;
    },

    /**
     * What may still be added for `drugId`.
     *
     * Accounts for the quantity ALREADY IN THE CART — the audit names this as
     * already correct and it stays correct.
     * @param {string} drugId
     * @returns {number}
     */
    availableOf(drugId) {
        return this.stockOf(drugId) - this.cartQtyOf(drugId);
    },

    /** Re-fold the ledger into per-drug positions. */
    async refreshStockPositions() {
        try {
            const { positions } = foldStock(await getAllEvents(db));
            this.stockPositions = positions;
        } catch (error) {
            console.error('Stock fold failed:', error);
            this.stockPositions = {};
        }
    },

    // ---- collection gate ----------------------------------------------------

    /**
     * Re-read the collection gate and reflect it in the UI (spec §7 order:
     * durability, identity, receipt supply).
     * @returns {Promise<{allowed:boolean, reason?:string}>}
     */
    async refreshGate() {
        let gate;
        try {
            gate = await collectionGate();
        } catch (error) {
            gate = { allowed: false, reason: 'engine-not-ready' };
            console.error('collection gate failed:', error);
        }
        this.collectionState = gate;

        const banner = this.elements.gateBanner;
        if (banner) {
            const slot = banner.querySelector('.alert-message') || banner;
            slot.textContent = gate.allowed ? '' : messageFor(gate.reason);
            banner.classList.toggle('hidden', gate.allowed);
        }

        this.updateTotal();
        return gate;
    },

    // Load drugs into select dropdown and grid
    async loadDrugs() {
        try {
            await this.refreshStockPositions();

            const { data, error } = await supabase
                .from('drugs')
                .select('*')
                .order('name');

            let drugs;
            if (!error && data) {
                await db.drugs.bulkPut(data);
                drugs = data;
            } else {
                drugs = await db.drugs.toArray();
            }

            // A DEACTIVATED drug stays in the stock fold and stays resolvable on an
            // old receipt (spec §3.3) — it is simply not offered for sale.
            this.allDrugs = drugs.filter(d => d.is_active !== false);
            this.drugsData = {};
            drugs.forEach(drug => { this.drugsData[drug.id] = drug; });

            this.populateDrugSelect(this.allDrugs.filter(d => this.stockOf(d.id) > 0));
            this.populateDrugsGrid(this.allDrugs);
            this.updateDrugStats(this.allDrugs);
        } catch (error) {
            console.error('Error loading drugs:', error);
            showStatus('Échec du chargement des médicaments', true);
        }
    },

    // Update drug statistics
    updateDrugStats(drugs) {
        const inStock = drugs.filter(d => this.stockOf(d.id) > 0).length;
        const lowStock = drugs.filter(d => {
            const position = this.stockOf(d.id);
            return position > 0 && position < LOW_STOCK_THRESHOLD;
        }).length;

        if (this.elements.drugsCount) this.elements.drugsCount.textContent = inStock;
        if (this.elements.lowStockCount) this.elements.lowStockCount.textContent = lowStock;
    },

    /**
     * Today's pharmacy figures, folded from the local ledger mirror — the same
     * events and the same fold as the dashboard and the reports (audit H2), with
     * unsent sales included and captioned (audit H6).
     */
    async loadMiniStats() {
        try {
            const today = businessDateOf(new Date().toISOString());
            const events = await getAllEvents(db);
            const todayEvents = events.filter(e => e && e.business_date === today);
            const fold = foldMoney(todayEvents);
            const pharmacy = fold.byChannel.pharmacy || { net: 0, saleCount: 0 };

            const unsentIds = new Set(await db.table('outbox').toCollection().primaryKeys());
            const unsent = todayEvents.filter(e => unsentIds.has(e.id) && e.channel === 'pharmacy');

            if (this.elements.todayCount) {
                this.elements.todayCount.textContent = pharmacy.saleCount;
            }
            if (this.elements.todayTotal) {
                // Un nombre issu du repli, jamais du texte saisi : l'unité est
                // un suffixe atténué, comme sur les autres chiffres-clés.
                this.elements.todayTotal.innerHTML =
                    `${fmtNo.format(pharmacy.net)} <span class="stats-cell-unit">F CFA</span>`;
            }
            if (this.elements.todayPending) {
                const text = unsent.length > 0
                    ? `${unsent.length} vente(s) non encore synchronisée(s) sont incluses dans ce montant`
                    : '';
                this.elements.todayPending.textContent = text;
                this.elements.todayPending.classList.toggle('hidden', text === '');
            }
        } catch (error) {
            console.error('Error folding mini stats:', error);
        }
    },

    // Populate the drugs grid with clickable cards
    populateDrugsGrid(drugs) {
        if (!this.elements.drugsGrid) return;

        if (this.elements.drugsLoading) {
            this.elements.drugsLoading.style.display = 'none';
        }

        this.elements.drugsGrid.innerHTML = '';

        if (drugs.length === 0) {
            // Direct `<p>` de la grille : `.items-grid > p` lui garde la largeur
            // de la grille et le centre, plutôt que de laisser un vide muet.
            this.elements.drugsGrid.innerHTML = '<p>Aucun médicament trouvé</p>';
            return;
        }

        drugs.forEach(drug => {
            const position = this.stockOf(drug.id);
            const card = document.createElement('div');
            card.className = 'item-card';
            card.dataset.id = drug.id;
            card.dataset.name = drug.name;
            card.dataset.price = drug.price;
            card.dataset.stock = String(position);
            card.dataset.unit = drug.unit || '';

            // Determine stock status. A negative position is shown as such — never
            // clamped to zero, which would invent stock (spec §4).
            let stockClass = 'stock-ok';
            let stockLabel = `${fmtNo.format(position)} en stock`;
            if (position <= 0) {
                stockClass = 'stock-out';
                stockLabel = 'Rupture';
                card.classList.add('out-of-stock');
            } else if (position < LOW_STOCK_THRESHOLD) {
                stockClass = 'stock-low';
                // « restants » et non « en stock » : le mot dit qu'il en reste peu,
                // là où le nombre seul demandait de connaître le seuil de tête.
                stockLabel = `${fmtNo.format(position)} restants`;
            }

            // L'unité passe sous le prix (« par comprimé ») : elle qualifie ce que
            // vaut UNE unité, pas l'état du stock.
            const unitLine = drug.unit
                ? `<div class="item-card-unit">par ${escapeHtml(drug.unit)}</div>`
                : '';

            card.innerHTML = `
                <div class="item-card-stockline ${stockClass}">
                    <span class="stock-dot"></span>
                    <span class="item-card-stocklabel">${escapeHtml(stockLabel)}</span>
                </div>
                <div class="item-card-name">${escapeHtml(drug.name)}</div>
                <div class="item-card-foot">
                    <div>
                        <div class="item-card-price">${fmtNo.format(drug.price)} F CFA</div>
                        ${unitLine}
                    </div>
                    <button class="item-card-add" type="button" aria-label="Ajouter" ${position <= 0 ? 'disabled' : ''}>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M12 5v14M5 12h14"/>
                        </svg>
                    </button>
                </div>
            `;

            // Click on card to add drug (if in stock) — out-of-stock disabling kept.
            // `closest`, et non `classList.contains` : le bouton enveloppe désormais
            // un `<svg>`, et c'est le `<path>` qui reçoit le clic.
            card.addEventListener('click', (e) => {
                if (!e.target.closest('.item-card-add') && this.stockOf(drug.id) > 0) {
                    this.addDrugFromCard(drug);
                }
            });

            const addBtn = card.querySelector('.item-card-add');
            if (addBtn && position > 0) {
                addBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.addDrugFromCard(drug);
                });
            }

            this.elements.drugsGrid.appendChild(card);
        });
    },

    // Filter drugs based on search and stock status
    filterDrugs(searchTerm, stockFilter) {
        let filtered = this.allDrugs;

        if (stockFilter && stockFilter !== 'all') {
            switch (stockFilter) {
                case 'in-stock':
                    filtered = filtered.filter(drug => this.stockOf(drug.id) > 0);
                    break;
                case 'low-stock':
                    filtered = filtered.filter(drug => {
                        const p = this.stockOf(drug.id);
                        return p > 0 && p < LOW_STOCK_THRESHOLD;
                    });
                    break;
                case 'out-of-stock':
                    filtered = filtered.filter(drug => this.stockOf(drug.id) <= 0);
                    break;
            }
        }

        // Plain substring matching — no regex on user input here (audit M6).
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(drug =>
                String(drug.name || '').toLowerCase().includes(term)
            );
        }

        this.populateDrugsGrid(filtered);
    },

    // Add drug from grid card (1 unit)
    addDrugFromCard(drug) {
        const price = toWholeFcfa(drug.price);
        if (price === null) {
            showStatus(`Prix invalide pour « ${drug.name} » — rechargez la liste des médicaments.`, true);
            return;
        }

        // Cart-side validation that accounts for what is already in the cart.
        if (this.availableOf(drug.id) <= 0) {
            showStatus('Stock insuffisant', true);
            return;
        }

        const existingIndex = this.selectedDrugs.findIndex(d => d.id === drug.id);
        if (existingIndex > -1) {
            this.selectedDrugs[existingIndex].quantity += 1;
        } else {
            this.selectedDrugs.push({
                id: drug.id,
                name: drug.name,
                price,
                unit: drug.unit || '',
                quantity: 1
            });
        }

        this.updateSelectedDrugsList();
        this.updateTotal();

        showStatus(`${drug.name} ajouté`, false);
    },

    // ============================================
    // AUTOCOMPLETE METHODS
    // ============================================

    // Handle autocomplete search
    handleAutocompleteSearch(query) {
        const clearBtn = this.elements.clearDrugSearch;

        if (query.length === 0) {
            this.hideAutocompleteDropdown();
            clearBtn?.classList.add('hidden');
            this.selectedDrug = null;
            this.elements.drugSearchInput?.classList.remove('has-selection');
            return;
        }

        clearBtn?.classList.remove('hidden');

        const term = query.toLowerCase();
        this.autocompleteResults = this.allDrugs.filter(drug =>
            String(drug.name || '').toLowerCase().includes(term)
        ).slice(0, 10);

        this.autocompleteFocusIndex = -1;
        this.renderAutocompleteDropdown();
        this.showAutocompleteDropdown();
    },

    // Handle keyboard navigation in autocomplete
    handleAutocompleteKeydown(e) {
        const dropdown = this.elements.drugAutocompleteDropdown;
        if (!dropdown || dropdown.classList.contains('hidden')) {
            if (e.key === 'Enter' && this.elements.drugSearchInput.value.length > 0) {
                e.preventDefault();
                this.handleAutocompleteSearch(this.elements.drugSearchInput.value);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                this.autocompleteFocusIndex = Math.min(
                    this.autocompleteFocusIndex + 1,
                    this.autocompleteResults.length - 1
                );
                this.updateAutocompleteFocus();
                break;

            case 'ArrowUp':
                e.preventDefault();
                this.autocompleteFocusIndex = Math.max(this.autocompleteFocusIndex - 1, -1);
                this.updateAutocompleteFocus();
                break;

            case 'Enter':
                e.preventDefault();
                if (this.autocompleteFocusIndex >= 0 && this.autocompleteResults[this.autocompleteFocusIndex]) {
                    this.selectDrugFromAutocomplete(this.autocompleteResults[this.autocompleteFocusIndex]);
                }
                break;

            case 'Escape':
                this.hideAutocompleteDropdown();
                break;
        }
    },

    /**
     * Render the autocomplete list.
     *
     * The match highlight builds a `RegExp` from the user's own typing, so the
     * query is ESCAPED (audit M6): typing `(` used to throw `SyntaxError`, and drug
     * names commonly contain parentheses. The drug NAME is escaped for HTML too
     * (audit M7) — and it is escaped BEFORE the highlight is applied, so the
     * `<mark>` tags we insert are the only markup in the string.
     */
    renderAutocompleteDropdown() {
        const dropdown = this.elements.drugAutocompleteDropdown;
        if (!dropdown) return;

        if (this.autocompleteResults.length === 0) {
            dropdown.innerHTML = `
                <div class="autocomplete-no-results">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <p>Aucun médicament trouvé</p>
                </div>
            `;
            return;
        }

        const query = this.elements.drugSearchInput?.value || '';
        const highlighter = query
            ? new RegExp(`(${escapeRegExp(escapeHtml(query))})`, 'gi')
            : null;

        dropdown.innerHTML = this.autocompleteResults.map((drug, index) => {
            const position = this.stockOf(drug.id);
            let stockClass = 'in-stock';
            let isDisabled = false;
            if (position <= 0) {
                stockClass = 'out-of-stock';
                isDisabled = true;
            } else if (position < LOW_STOCK_THRESHOLD) {
                stockClass = 'low-stock';
            }

            const safeName = escapeHtml(drug.name);
            const highlightedName = highlighter
                ? safeName.replace(highlighter, '<mark>$1</mark>')
                : safeName;

            return `
                <div class="autocomplete-item ${isDisabled ? 'disabled' : ''}"
                     data-index="${index}"
                     data-id="${escapeHtml(drug.id)}">
                    <div class="autocomplete-item-info">
                        <div class="autocomplete-item-name">${highlightedName}</div>
                        <div class="autocomplete-item-meta">
                            <span class="autocomplete-item-stock ${stockClass}">
                                ${isDisabled ? 'Rupture' : `${escapeHtml(position)} ${escapeHtml(drug.unit || '')}`}
                            </span>
                        </div>
                    </div>
                    <span class="autocomplete-item-price">${fmtNo.format(drug.price)} F CFA</span>
                </div>
            `;
        }).join('');

        // Listeners, not interpolated inline `onclick` attributes.
        dropdown.querySelectorAll('.autocomplete-item').forEach(node => {
            if (node.classList.contains('disabled')) return;
            node.addEventListener('click', () => {
                const index = Number(node.dataset.index);
                this.selectDrugFromAutocomplete(this.autocompleteResults[index]);
            });
        });
    },

    // Update focus state in autocomplete
    updateAutocompleteFocus() {
        const items = this.elements.drugAutocompleteDropdown?.querySelectorAll('.autocomplete-item');
        if (!items) return;

        items.forEach((item, index) => {
            if (index === this.autocompleteFocusIndex) {
                item.classList.add('focused');
                item.scrollIntoView({ block: 'nearest' });
            } else {
                item.classList.remove('focused');
            }
        });
    },

    // Select drug from autocomplete
    selectDrugFromAutocomplete(drug) {
        const position = drug ? this.stockOf(drug.id) : 0;
        if (!drug || position <= 0) {
            showStatus('Ce médicament est en rupture de stock', true);
            return;
        }

        this.selectedDrug = drug;

        this.elements.drugSearchInput.value = drug.name;
        this.elements.drugSearchInput.classList.add('has-selection');
        this.elements.drugSelect.value = drug.id;

        const available = this.availableOf(drug.id);
        this.elements.quantityInput.max = available;
        this.elements.quantityInput.value = 1;
        this.elements.quantityInput.disabled = false;
        this.elements.maxQuantity.textContent = `Max: ${available} ${drug.unit || ''}`.trim();
        this.elements.maxQuantity.className = position < LOW_STOCK_THRESHOLD
            ? 'stock-indicator stock-indicator--low'
            : 'stock-indicator stock-indicator--high';

        this.hideAutocompleteDropdown();
        this.elements.quantityInput.focus();
        this.elements.quantityInput.select();
    },

    // Clear drug selection
    clearDrugSelection() {
        this.selectedDrug = null;
        if (this.elements.drugSearchInput) {
            this.elements.drugSearchInput.value = '';
            this.elements.drugSearchInput.classList.remove('has-selection');
        }
        if (this.elements.drugSelect) this.elements.drugSelect.value = '';
        if (this.elements.quantityInput) {
            this.elements.quantityInput.value = '';
            this.elements.quantityInput.disabled = true;
        }
        if (this.elements.maxQuantity) this.elements.maxQuantity.textContent = '';
        this.elements.clearDrugSearch?.classList.add('hidden');
        this.hideAutocompleteDropdown();
        this.elements.drugSearchInput?.focus();
    },

    // Show autocomplete dropdown
    showAutocompleteDropdown() {
        this.elements.drugAutocompleteDropdown?.classList.remove('hidden');
    },

    // Hide autocomplete dropdown
    hideAutocompleteDropdown() {
        this.elements.drugAutocompleteDropdown?.classList.add('hidden');
        this.autocompleteFocusIndex = -1;
    },

    // ============================================
    // END AUTOCOMPLETE METHODS
    // ============================================

    // Populate the drug select dropdown (legacy fallback UI)
    populateDrugSelect(drugs) {
        if (!this.elements.drugSelect) return;
        this.elements.drugSelect.innerHTML = '<option value="">-- Choisir un médicament --</option>';

        drugs.forEach(drug => {
            const position = this.stockOf(drug.id);
            const option = document.createElement('option');
            option.value = drug.id;
            option.textContent = `${drug.name} - ${formatCurrency(drug.price)} (Stock: ${position} ${drug.unit || ''})`;
            option.dataset.name = drug.name;
            option.dataset.price = drug.price;
            option.dataset.stock = String(position);
            option.dataset.unit = drug.unit || '';
            this.elements.drugSelect.appendChild(option);
        });
    },

    // Add selected drug to the list
    addDrug() {
        if (!this.selectedDrug) {
            showStatus('Veuillez sélectionner un médicament', true);
            this.elements.drugSearchInput?.focus();
            return;
        }

        const drug = this.selectedDrug;
        const quantity = parseInt(this.elements.quantityInput.value, 10);

        if (!Number.isInteger(quantity) || quantity <= 0) {
            showStatus('Veuillez entrer une quantité valide', true);
            this.elements.quantityInput?.focus();
            return;
        }

        const price = toWholeFcfa(drug.price);
        if (price === null) {
            showStatus(`Prix invalide pour « ${drug.name} » — rechargez la liste des médicaments.`, true);
            return;
        }

        // Cart-side stock validation, accounting for what is already in the cart.
        const maxAvailable = this.availableOf(drug.id);
        if (quantity > maxAvailable) {
            showStatus(`Seulement ${maxAvailable} ${drug.unit || ''} disponible(s) en stock`.replace(/\s+/g, ' '), true);
            return;
        }

        const existingIndex = this.selectedDrugs.findIndex(d => d.id === drug.id);
        if (existingIndex > -1) {
            this.selectedDrugs[existingIndex].quantity += quantity;
        } else {
            this.selectedDrugs.push({
                id: drug.id,
                name: drug.name,
                price,
                unit: drug.unit || '',
                quantity
            });
        }

        this.updateSelectedDrugsList();
        this.updateTotal();

        this.clearDrugSelection();
        showStatus(`${drug.name} (x${quantity}) ajouté`, false);
    },

    // Update the display of selected drugs
    updateSelectedDrugsList() {
        if (!this.elements.selectedDrugsList) return;

        this.elements.selectedDrugsList.innerHTML = '';

        // Update cart count badge. Le zéro reste écrit — un compteur absent se
        // lit comme un widget cassé, pas comme « rien ». C'est la couleur qui
        // change, pas la présence du nombre.
        if (this.elements.cartCount) {
            this.elements.cartCount.textContent = this.selectedDrugs.length;
            this.elements.cartCount.classList.toggle('has-items', this.selectedDrugs.length > 0);
        }

        if (this.selectedDrugs.length === 0) {
            // `.cart-empty` doit rester l'UNIQUE enfant du corps du panier :
            // c'est son `margin:auto` qui le centre. Il garde la taille du
            // panier plein et dit quoi faire (invariant « états vides »).
            this.elements.selectedDrugsList.innerHTML = `
                <div class="cart-empty">
                    <div class="cart-empty-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
                        </svg>
                    </div>
                    <p class="cart-empty-text">Aucun médicament sélectionné</p>
                    <p class="cart-empty-hint">Choisissez un médicament dans le catalogue pour démarrer la vente.</p>
                </div>
            `;
            return;
        }

        this.selectedDrugs.forEach((drug, index) => {
            const drugDiv = document.createElement('div');
            drugDiv.className = 'cart-item';

            // La ligne de détail porte la quantité, l'unité ET le prix unitaire :
            // « 20 cp × 100 F CFA ». Sans le prix unitaire, un total de 2 000 sous
            // « 20 cp » ne se vérifie pas de tête au comptoir.
            const qtyUnit = `${fmtNo.format(drug.quantity)} ${drug.unit || ''}`.trim();

            drugDiv.innerHTML = `
                <div class="cart-item-info">
                    <span class="cart-item-name">${escapeHtml(drug.name)}</span>
                    <span class="cart-item-qty">${escapeHtml(qtyUnit)} × ${fmtNo.format(drug.price)} F CFA</span>
                </div>
                <div class="cart-item-actions">
                    <span class="cart-item-price">${fmtNo.format(drug.price * drug.quantity)} F CFA</span>
                    <button class="cart-item-remove" type="button" data-index="${index}" aria-label="Supprimer" title="Supprimer">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                    </button>
                </div>
            `;

            drugDiv.querySelector('.cart-item-remove')
                ?.addEventListener('click', () => this.removeDrug(index));

            this.elements.selectedDrugsList.appendChild(drugDiv);
        });
    },

    // Remove a drug from the list
    removeDrug(index) {
        this.selectedDrugs.splice(index, 1);
        this.updateSelectedDrugsList();
        this.updateTotal();
    },

    // Update total amount
    updateTotal() {
        this.currentTotal = this.selectedDrugs.reduce((total, drug) => {
            return total + (drug.price * drug.quantity);
        }, 0);

        if (this.elements.totalAmount) {
            this.elements.totalAmount.innerHTML =
                `${fmtNo.format(this.currentTotal)} <span class="cart-total-unit">F CFA</span>`;
        }

        const hasItems = this.selectedDrugs.length > 0;
        const gateAllows = !this.collectionState || this.collectionState.allowed;

        if (this.elements.submitBtn) {
            this.elements.submitBtn.disabled = !hasItems || !gateAllows;
        }
        if (this.elements.printPreviewBtn) {
            this.elements.printPreviewBtn.disabled = !hasItems;
        }
    },

    // Clear the transaction form
    clearTransaction() {
        this.selectedDrugs = [];
        this.currentTotal = 0;
        this.elements.transactionForm?.reset();
        this.updateSelectedDrugsList();
        this.updateTotal();
        this.clearDrugSelection();
        if (this.elements.customerAge) this.elements.customerAge.value = '';
        if (this.elements.customerSex) this.elements.customerSex.value = '';
    },

    /**
     * Project the cart into spec §6 line items — `{item_id, item_kind:'drug', qty,
     * unit_price, line_total}`.
     *
     * `item_id` is MANDATORY and is audit C4 closed at the source: the legacy
     * pharmacy line carried no id at all, so every offline sale moved no stock and
     * was still marked synced.
     * @returns {object[]}
     * @throws {Error} With a French message, when a line is not whole FCFA.
     */
    buildLineItems() {
        return this.selectedDrugs.map(drug => {
            const unitPrice = toWholeFcfa(drug.price);
            const qty = toWholeFcfa(drug.quantity);
            if (unitPrice === null || qty === null || qty <= 0) {
                throw new Error(
                    `Ligne invalide pour « ${drug.name} » : prix ou quantité non entiers. Retirez-la du panier et rechargez la liste des médicaments.`
                );
            }
            return {
                item_id: drug.id,
                item_kind: 'drug',
                name: drug.name,
                qty,
                unit_price: unitPrice,
                line_total: qty * unitPrice
            };
        });
    },

    // Handle form submission
    async handleSubmit(e) {
        e.preventDefault();

        if (this.selectedDrugs.length === 0) {
            showStatus('Ajoutez au moins un médicament', true);
            return;
        }

        const customerName = this.elements.customerName.value.trim();
        if (!customerName) {
            showStatus('Saisissez le nom du client', true);
            this.elements.customerName.focus();
            return;
        }

        // THE GATE, before anything is minted or written (spec §7).
        const gate = await this.refreshGate();
        if (!gate.allowed) {
            showStatus(messageFor(gate.reason), true);
            return;
        }

        const engine = getEngine();
        if (!engine || !engine.deviceId) {
            showStatus(messageFor('device-not-provisioned'), true);
            return;
        }

        let items;
        try {
            items = this.buildLineItems();
        } catch (error) {
            showStatus(error.message, true);
            return;
        }
        const amount = items.reduce((sum, line) => sum + line.line_total, 0);

        // Disabled BEFORE the await — kept from the pre-rewrite code.
        this.elements.submitBtn.disabled = true;
        // innerHTML, pas textContent : le bouton contient un <svg> que
        // textContent détruirait définitivement à la première vente.
        const originalLabel = this.elements.submitBtn.innerHTML;
        this.elements.submitBtn.textContent = 'Enregistrement…';

        /** @type {{id:string, receipt_no:number}|null} */
        let saved = null;
        const deviceTime = new Date().toISOString();

        try {
            // ONE call, ONE event. It carries the money AND the goods: stock moves
            // because this row exists, not because a second UPDATE succeeded. That
            // is C2 and C3 gone together — there is no second write to race and no
            // second write to fail silently.
            saved = await recordSale(db, {
                channel: 'pharmacy',
                amount,
                items,
                device_id: engine.deviceId,
                device_time: deviceTime,
                business_date: businessDateOf(deviceTime),
                patient_name: customerName,
                book_no: null,
                collected_by: null
            });
        } catch (error) {
            console.error('Sale refused:', error);
            showStatus(messageForError(error), true);
            return;
        } finally {
            this.elements.submitBtn.disabled = false;
            this.elements.submitBtn.innerHTML = originalLabel;
            this.updateTotal();
        }

        // ─── POST-COMMIT ────────────────────────────────────────────────────
        // Outside the try above (audit M5). The money and the stock movement are
        // recorded; nothing below may report the sale as failed.

        const receipt = {
            receiptNo: saved.receipt_no,
            receiptLabel: formatReceiptNo(saved.receipt_no),
            dateIso: deviceTime,
            customer: {
                name: customerName,
                prescription: this.elements.prescriptionNo?.value || '',
                age: this.elements.customerAge?.value || '',
                sex: this.elements.customerSex?.value || ''
            },
            lines: items,
            total: amount,
            committed: true
        };

        this.clearTransaction();   // audit H3
        this.showReceipt(receipt);

        showStatus(`Reçu n° ${receipt.receiptLabel} — ${UI_STRINGS.RECEIPT_ISSUED}`, false);

        void this.afterCommit();
    },

    /**
     * Post-commit side effects, each independently guarded (audit M5). The stock
     * re-fold is here rather than in the write path: the sale is already the
     * authority, and this only refreshes what the screen shows.
     */
    async afterCommit() {
        const engine = getEngine();
        if (engine) {
            try { await engine.kick(); } catch (error) { console.error('sync kick failed:', error); }
        }
        try {
            await this.refreshStockPositions();
            this.populateDrugsGrid(this.allDrugs);
            this.populateDrugSelect(this.allDrugs.filter(d => this.stockOf(d.id) > 0));
            this.updateDrugStats(this.allDrugs);
        } catch (error) {
            console.error('stock refresh failed:', error);
        }
        try { await this.loadMiniStats(); } catch (error) { console.error('mini stats failed:', error); }
        try { await this.refreshGate(); } catch (error) { console.error('gate refresh failed:', error); }
        try {
            const userRole = window.AppCore?.getCurrentRole?.();
            if (userRole === 'admin' && window.AppCore?.getDashboardStats) {
                window.AppCore.renderDashboardStats(await window.AppCore.getDashboardStats());
                // Same reason as `cashierATransactions.afterCommit`: the feed and
                // the reminders fold the same ledger and must move with the tiles.
                await window.UI?.refreshDashboardFeeds?.();
            }
        } catch (error) {
            console.error('dashboard refresh failed:', error);
        }
    },

    // Show receipt preview (before submitting)
    showReceiptPreview() {
        if (this.selectedDrugs.length === 0) {
            showStatus('Aucun médicament sélectionné', true);
            return;
        }

        let items;
        try {
            items = this.buildLineItems();
        } catch (error) {
            showStatus(error.message, true);
            return;
        }

        this.showReceipt({
            receiptNo: null,
            receiptLabel: 'APERÇU',
            dateIso: new Date().toISOString(),
            customer: {
                name: this.elements.customerName?.value || 'Non spécifié',
                prescription: this.elements.prescriptionNo?.value || '',
                age: this.elements.customerAge?.value || '',
                sex: this.elements.customerSex?.value || ''
            },
            lines: items,
            total: items.reduce((sum, line) => sum + line.line_total, 0),
            committed: false
        });
    },

    /**
     * Render the receipt. A COMMITTED receipt locks the modal — « Nouvelle Vente »
     * is the only way out (audit H3).
     * @param {object} receipt
     */
    showReceipt(receipt) {
        this.receiptCommitted = receipt.committed === true;

        const unitOf = (line) => {
            const drug = this.drugsData[line.item_id];
            return drug && drug.unit ? drug.unit : '';
        };

        // Deux lignes par article : le nom sur toute la largeur, puis
        // « qté unité × prix unitaire » à gauche et le total de ligne à droite.
        // Les nombres du corps du reçu sont NUS — seul le TOTAL porte « F CFA »,
        // sur un rouleau de 80 mm chaque répétition coûte de la largeur.
        const items = receipt.lines.map(line => {
            const qtyUnit = `${fmtNo.format(line.qty)} ${unitOf(line)}`.trim();
            return `
                    <div class="rt-item">
                        <div class="rt-item-name">${escapeHtml(line.name)}</div>
                        <div class="rt-item-line">
                            <span>${escapeHtml(qtyUnit)} × ${fmtNo.format(line.unit_price)}</span>
                            <span>${fmtNo.format(line.line_total)}</span>
                        </div>
                    </div>`;
        }).join('');

        // Une ligne de méta absente vaut mieux qu'une ligne vide : le reçu ne
        // promet pas un champ qu'il n'a pas.
        const metaRow = (label, value, strong) => value
            ? `<div class="rt-row${strong ? ' rt-row--strong' : ''}"><span>${escapeHtml(label)}</span><span>${escapeHtml(value)}</span></div>`
            : '';

        const sexLabel = receipt.customer.sex === 'M' ? 'Masculin'
            : receipt.customer.sex === 'F' ? 'Féminin' : '';
        const ageLabel = receipt.customer.age ? `${receipt.customer.age} ans` : '';
        const ageSex = [ageLabel, sexLabel].filter(Boolean).join(' · ');

        const paperBookNote = receipt.committed
            ? `<div class="rt-note">${escapeHtml(UI_STRINGS.RECEIPT_ISSUED)}</div>`
            : '';

        const receiptHtml = `
            <div class="receipt-thermal" id="printable-pharmacy-receipt">
                <div class="rt-head">
                    <img class="rt-mark" src="assets/logo-mark-black.png" alt="">
                    <div class="rt-brand">CMA OMBESSA</div>
                    <div class="rt-line">Centre Médical d'Arrondissement</div>
                    <div class="rt-line">Tél: +237 695 615 932</div>
                </div>
                <div class="rt-rule"></div>
                <div class="rt-id">
                    <div class="rt-doctype">REÇU — PHARMACIE</div>
                    <div class="rt-number">${escapeHtml(receipt.receiptLabel)}</div>
                </div>
                <div class="rt-rule"></div>
                <div class="rt-meta">
                    ${metaRow('Date', new Date(receipt.dateIso).toLocaleString('fr-CM'), false)}
                    ${metaRow('Client', receipt.customer.name, true)}
                    ${metaRow('Ordonnance', receipt.customer.prescription, false)}
                    ${metaRow('Âge / Sexe', ageSex, false)}
                </div>
                <div class="rt-rule"></div>
                <div class="rt-cols"><span>MÉDICAMENT</span><span>MONTANT</span></div>
                <div class="rt-items">${items}
                </div>
                <div class="rt-solid"></div>
                <div class="rt-total"><span>TOTAL</span><span>${fmtNo.format(receipt.total)} F CFA</span></div>
                <div class="rt-double"></div>
                <div class="rt-thanks">Merci de votre achat.</div>
                <div class="rt-important">
                    <div class="rt-important-title">IMPORTANT</div>
                    <div class="rt-important-body">Conservez ce reçu.<br>Aucun retour sans reçu.</div>
                </div>
                ${paperBookNote}
            </div>`;

        // Le bord déchiré n'existe que dans l'aperçu — `printReceipt()` ne
        // récupère que `#printable-pharmacy-receipt`, jamais le zigzag.
        this.elements.receiptContent.innerHTML = `${receiptHtml}
            <div class="rt-zigzag"></div>`;

        if (this.elements.closeReceiptBtn) {
            this.elements.closeReceiptBtn.classList.toggle('hidden', this.receiptCommitted);
        }
        this.elements.receiptModal.classList.remove('hidden');
    },

    // Print receipt
    printReceipt() {
        const printContent = document.getElementById('printable-pharmacy-receipt');
        if (!printContent) return;
        const printWindow = window.open('', '', 'height=600,width=400');
        if (!printWindow) {
            showStatus('Impression bloquée par le navigateur — autorisez les fenêtres pop-up.', true);
            return;
        }

        // UN document complet, écrit en une fois. `css/fonts.css` est lié en
        // relatif : la pop-up hérite de l'URL de base de l'ouvreur, la feuille
        // est de même origine et déjà en cache du service worker — aucun CDN.
        // Poppins doit survivre à l'impression : c'est la police de l'en-tête,
        // du numéro de reçu et du TOTAL.
        //
        // `outerHTML`, pas `innerHTML` : la classe `receipt-thermal` porte la
        // largeur de 80 mm et les marges du rouleau.
        printWindow.document.write(`<!doctype html>
<html lang="fr">
<head>
<meta charset="utf-8">
<title>Reçu Pharmacie</title>
<link rel="stylesheet" href="css/fonts.css">
<style>${PRINT_CSS}</style>
</head>
<body>${printContent.outerHTML}</body>
</html>`);

        printWindow.document.close();

        // Le logo et Poppins arrivent par le réseau ; `print()` appelé dans la
        // foulée de `document.close()` court après eux. On laisse le document
        // finir, puis les polices — et un délai de garde imprime quand même si
        // une ressource traîne : un reçu sans logo vaut mieux qu'un reçu qui ne
        // sort pas. `readyState` est testé d'abord pour que le cas normal
        // n'attende rien du tout.
        let printed = false;
        const doPrint = () => {
            if (printed) return;
            printed = true;
            try {
                printWindow.focus();
                printWindow.print();
            } catch (error) {
                console.error('print failed:', error);
            }
        };
        const whenLoaded = () => {
            const fonts = printWindow.document.fonts;
            if (fonts && fonts.ready) fonts.ready.then(doPrint, doPrint);
            else doPrint();
        };

        if (printWindow.document.readyState === 'complete') whenLoaded();
        else printWindow.addEventListener('load', whenLoaded, { once: true });
        printWindow.setTimeout(doPrint, 3000);

        // Deliberately NOT closed here — closing right after `print()` cancels the
        // job on several browsers.
    },

    /** Close the modal — refused while a committed receipt is showing (audit H3). */
    closeReceiptModal() {
        if (this.receiptCommitted) {
            showStatus('Utilisez « Nouvelle Vente » pour continuer.', false);
            return;
        }
        this.elements.receiptModal.classList.add('hidden');
    },

    // Start new transaction
    startNewTransaction() {
        this.receiptCommitted = false;
        if (this.elements.closeReceiptBtn) this.elements.closeReceiptBtn.classList.remove('hidden');
        this.elements.receiptModal.classList.add('hidden');
        this.clearTransaction();
        this.elements.customerName?.focus();
        void this.refreshGate();
    }
};

// Make CashierBTransactions available globally
window.CashierBTransactions = CashierBTransactions;
